import type { Engine } from "../../../engine/Engine";
import type { SugarModel } from "../../../loader/SugarModel";
import type { CONFIGURATOR_MODEL_TYPE } from "../../../core/configurator/ConfiguratorData";

const REFRESH_EVENTS = [
  "model-added",
  "material-changed",
  "refresh-changed",
  "part-changed",
  "side-changed",
  "model-removed",
] as const;

export type ConfiguratorStateSync = {
  onModel: (model: CONFIGURATOR_MODEL_TYPE | undefined) => void;
  onScene: (scene: unknown) => void;
  onSelectedModel: (model: SugarModel | undefined) => void;
  onRefresh: () => void;
};

/**
 * Holds configurator document/selection mirrors and pushes changes through
 * {@link ConfiguratorStateSync} into messaging stores (MV-style single channel).
 */
export class ConfiguratorObservableState {
  model?: CONFIGURATOR_MODEL_TYPE;
  scene?: unknown;
  selectedModel?: SugarModel;
  private engine?: Engine;

  constructor(private readonly sync: ConfiguratorStateSync) {}

  bindEngine(engine: Engine): void {
    if (this.engine === engine) return;
    this.unbindEngine();
    this.engine = engine;
    for (const event of REFRESH_EVENTS) {
      engine.addEventListener(event, this.onRefresh);
    }
    engine.addEventListener("scene-changed", this.onSceneChanged);
  }

  setModel(model: CONFIGURATOR_MODEL_TYPE): void {
    this.model = model;
    this.sync.onModel(model);
  }

  setScene(scene: unknown): void {
    this.scene = scene;
    this.sync.onScene(scene);
  }

  setSelectedModel(model: SugarModel | undefined): void {
    this.selectedModel = model;
    this.sync.onSelectedModel(model);
  }

  refresh(): void {
    this.sync.onRefresh();
  }

  dispose(): void {
    this.unbindEngine();
  }

  private unbindEngine(): void {
    if (!this.engine) return;
    for (const event of REFRESH_EVENTS) {
      this.engine.removeEventListener(event, this.onRefresh);
    }
    this.engine.removeEventListener("scene-changed", this.onSceneChanged);
    this.engine = undefined;
  }

  private readonly onRefresh = (): void => this.refresh();

  private readonly onSceneChanged = (event: Event): void => {
    const detail = (event as CustomEvent).detail;
    if (detail) this.setScene(detail);
  };
}
